/**
 * "A SIMPLE TO USE report builder that can report on anything AeroGauge can
 * measure. Remember SIMPLE and PRETTY."
 *
 * What it replaced was a form, in form order: Name, Description, then a
 * "Select a type…" dropdown, and nothing else until you had chosen. It asked
 * what to call the report before you knew what it was, hid seventeen subjects
 * in a dropdown, listed all fields as a grid of bare checkboxes, and rendered
 * conditions as three dropdowns whose middle one offered "eq", "ne", "gte".
 *
 * The changes are all the same idea — say it the way a person would:
 *
 *  1. SUBJECT FIRST, as searchable tiles. You pick what you are reporting on
 *     before anything else, because every other choice depends on it. Name
 *     comes last, pre-filled, and only matters if you save.
 *  2. FIELDS AS CHIPS, sensible ones already on. Click to add or remove. A
 *     grid of forty checkboxes is a form; chips are a sentence you are editing.
 *  3. CONDITIONS IN ENGLISH. "Status is not online", not "status ne online".
 *     Bare operator codes are the single clearest sign a screen was built for
 *     whoever wrote the query layer.
 *  4. LIVE PREVIEW. Every change re-runs, debounced. You should never have to
 *     press a button to find out whether you built the right thing — that is
 *     how people end up saving a report that returns nothing.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/client";
import ActionButton from "../components/ActionButton";
import { formatDateTime } from "../utils/datetime";

export type BuilderColumn = {
  key: string;
  label: string;
  type: "string" | "int" | "float" | "bool" | "datetime" | "enum";
  enum_choices: string[] | null;
  filterable: boolean;
};
export type BuilderType = {
  key: string;
  label: string;
  description: string;
  category: string;
  default_columns: string[];
  columns: BuilderColumn[];
  ops_by_type: Record<string, string[]>;
};
export type BuilderFilter = { field: string; op: string; value: any };
export type BuilderDraft = {
  name: string;
  description: string;
  report_type: string;
  columns: string[];
  filters: BuilderFilter[];
  order_by: { field: string; direction: "asc" | "desc" } | null;
  row_limit: number;
};

/** Operator codes, said the way a person would.
 *
 * Every code the server accepts is here — checked against ops_by_type across
 * all 17 report types, not guessed. An unmapped code would render as itself,
 * which is the bug being fixed, so the fallback is deliberately the raw code:
 * visibly wrong beats silently missing. */
const OP_LABEL: Record<string, string> = {
  eq: "is",
  ne: "is not",
  contains: "contains",
  starts_with: "starts with",
  in: "is any of",
  lt: "is before",
  gt: "is after",
  gte: "is at least",
  lte: "is at most",
  last_n_days: "in the last (days)",
};
/** Numbers read differently from dates: "is at least 8" not "is after 8". */
const OP_LABEL_NUMERIC: Record<string, string> = {
  lt: "is less than",
  gt: "is more than",
  gte: "is at least",
  lte: "is at most",
};

function opLabel(op: string, colType?: string) {
  if (colType === "int" || colType === "float") return OP_LABEL_NUMERIC[op] || OP_LABEL[op] || op;
  return OP_LABEL[op] || op;
}

type Props = {
  types: BuilderType[];
  initial?: Partial<BuilderDraft>;
  /** Passed straight to the preview call so the customer selector is honoured. */
  scopeCfg?: any;
  onCancel: () => void;
  onSave: (d: BuilderDraft) => void | Promise<void>;
  onDownload: (d: BuilderDraft, fmt: "csv" | "pdf") => void | Promise<void>;
  saving?: boolean;
};

export default function SimpleReportBuilder({
  types,
  initial,
  scopeCfg,
  onCancel,
  onSave,
  onDownload,
  saving,
}: Props) {
  const [typeKey, setTypeKey] = useState(initial?.report_type || "");
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [columns, setColumns] = useState<string[]>(initial?.columns || []);
  const [filters, setFilters] = useState<BuilderFilter[]>(initial?.filters || []);
  const [orderBy, setOrderBy] = useState(initial?.order_by || null);
  const [limit, setLimit] = useState(initial?.row_limit || 1000);
  const [subjectSearch, setSubjectSearch] = useState("");
  const [showAllFields, setShowAllFields] = useState(false);

  const [preview, setPreview] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const t = useMemo(() => types.find((x) => x.key === typeKey), [types, typeKey]);
  const colOf = useCallback(
    (key: string) => t?.columns.find((c) => c.key === key),
    [t],
  );

  const pickSubject = (nt: BuilderType) => {
    setTypeKey(nt.key);
    // Start from a runnable report, not an empty one. Choosing a subject and
    // being shown nothing is what made the old form feel like work.
    setColumns(nt.default_columns?.length ? nt.default_columns : nt.columns.slice(0, 6).map((c) => c.key));
    setFilters([]);
    setOrderBy(null);
    if (!name.trim()) setName(nt.label);
  };

  // ── Live preview, debounced ───────────────────────────────────────────────
  const debounce = useRef<any>(null);
  useEffect(() => {
    if (!typeKey || columns.length === 0) {
      setPreview(null);
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setPreviewing(true);
      setPreviewError("");
      try {
        const r = await api.post(
          "/api/v1/reports-builder/preview",
          {
            report_type: typeKey,
            columns,
            // A half-typed condition ("Status is …") would 400 on every
            // keystroke. Incomplete rows are simply not sent yet.
            filters: filters.filter((f) => f.field && f.op && f.value !== "" && f.value != null),
            order_by: orderBy,
            row_limit: Math.min(limit, 50),
          },
          scopeCfg,
        );
        setPreview(r.data);
      } catch (e: any) {
        setPreviewError(e?.response?.data?.detail || "Could not run that");
      } finally {
        setPreviewing(false);
      }
    }, 450);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [typeKey, columns, filters, orderBy, limit, scopeCfg]);

  const draft = (): BuilderDraft => ({
    name: name.trim() || t?.label || "Report",
    description,
    report_type: typeKey,
    columns,
    filters: filters.filter((f) => f.field && f.op && f.value !== "" && f.value != null),
    order_by: orderBy,
    row_limit: limit,
  });

  // ── Subject picker ────────────────────────────────────────────────────────
  if (!t) {
    const q = subjectSearch.trim().toLowerCase();
    const shown = q
      ? types.filter((x) =>
          `${x.label} ${x.description} ${x.category} ${x.columns.map((c) => c.label).join(" ")}`
            .toLowerCase()
            .includes(q),
        )
      : types;
    const groups = new Map<string, BuilderType[]>();
    shown.forEach((x) => {
      const k = x.category || "Other";
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(x);
    });
    return (
      <div>
        <div className="flex items-start justify-between mb-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Build a report</h1>
          <button
            onClick={onCancel}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            ← Back to reports
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
          Start with what you want to report on. You can change every field and add conditions
          next — and you will see the results as you go.
        </p>

        <input
          value={subjectSearch}
          onChange={(e) => setSubjectSearch(e.target.value)}
          placeholder="Search subjects…"
          className="w-full max-w-md mb-5 px-3 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
        />

        {Array.from(groups.entries()).map(([cat, items]) => (
          <div key={cat} className="mb-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2.5">
              {cat}
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((x) => (
                <button
                  key={x.key}
                  onClick={() => pickSubject(x)}
                  className="text-left rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all"
                >
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {x.label}
                  </div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                    {x.description}
                  </div>
                  <div className="mt-2 text-[11px] text-gray-400 dark:text-gray-600">
                    {x.columns.length} fields available
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
        {shown.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing matches that.</p>
        )}
      </div>
    );
  }

  // ── Build ─────────────────────────────────────────────────────────────────
  const filterable = t.columns.filter((c) => c.filterable);
  const visibleFields = showAllFields ? t.columns : t.columns.filter((c) => columns.includes(c.key) || (t.default_columns || []).includes(c.key));

  const addFilter = () => {
    const c = filterable[0];
    if (!c) return;
    const ops = t.ops_by_type[c.type] || ["eq"];
    setFilters([...filters, { field: c.key, op: ops[0], value: "" }]);
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {initial?.report_type ? "Edit report" : "Build a report"}
          </h1>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
            <span>
              Reporting on <strong className="text-gray-700 dark:text-gray-200">{t.label}</strong>
            </span>
            <button
              onClick={() => setTypeKey("")}
              className="text-blue-600 dark:text-blue-400 hover:underline text-xs"
            >
              change
            </button>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          ← Back to reports
        </button>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] items-start">
        {/* Controls */}
        <div className="space-y-4">
          {/* Fields */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Columns to show
              </h2>
              <span className="text-[11px] text-gray-400">{columns.length} selected</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {visibleFields.map((c) => {
                const on = columns.includes(c.key);
                return (
                  <button
                    key={c.key}
                    onClick={() =>
                      setColumns(
                        on ? columns.filter((k) => k !== c.key) : [...columns, c.key],
                      )
                    }
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      on
                        ? "bg-blue-600 border-blue-600 text-white"
                        : "bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-400"
                    }`}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs">
              <button
                onClick={() => setShowAllFields((v) => !v)}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                {showAllFields
                  ? "Show fewer"
                  : `Show all ${t.columns.length} fields`}
              </button>
              <button
                onClick={() => setColumns(t.default_columns || [])}
                className="text-gray-500 dark:text-gray-400 hover:underline"
              >
                Reset
              </button>
            </div>
            {columns.length === 0 && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                Pick at least one column.
              </p>
            )}
          </section>

          {/* Conditions */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex items-center justify-between mb-2.5">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Only include rows where…
              </h2>
              <button
                onClick={addFilter}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                + Add condition
              </button>
            </div>
            {filters.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                No conditions — every row is included.
              </p>
            )}
            <div className="space-y-2">
              {filters.map((f, i) => {
                const c = colOf(f.field);
                const ops = c ? t.ops_by_type[c.type] || [] : [];
                const update = (patch: Partial<BuilderFilter>) =>
                  setFilters(filters.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                return (
                  <div
                    key={i}
                    className="flex flex-wrap items-center gap-1.5 rounded-lg bg-gray-50 dark:bg-gray-900/50 p-2"
                  >
                    <select
                      value={f.field}
                      onChange={(e) => {
                        const nc = t.columns.find((x) => x.key === e.target.value);
                        const nops = nc ? t.ops_by_type[nc.type] || [] : [];
                        update({ field: e.target.value, op: nops[0] || "eq", value: "" });
                      }}
                      className="text-xs border rounded px-1.5 py-1 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    >
                      {filterable.map((x) => (
                        <option key={x.key} value={x.key}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                    <select
                      value={f.op}
                      onChange={(e) => update({ op: e.target.value })}
                      className="text-xs border rounded px-1.5 py-1 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                    >
                      {ops.map((o) => (
                        <option key={o} value={o}>
                          {opLabel(o, c?.type)}
                        </option>
                      ))}
                    </select>
                    {/* The value control follows the field's type: a dropdown
                        for enums and booleans, a date picker for dates. Typing
                        "true" into a text box is how a filter silently matches
                        nothing. */}
                    {c?.type === "bool" ? (
                      <select
                        value={String(f.value)}
                        onChange={(e) => update({ value: e.target.value === "true" })}
                        className="text-xs border rounded px-1.5 py-1 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : c?.type === "enum" && c.enum_choices?.length && f.op !== "in" ? (
                      <select
                        value={f.value ?? ""}
                        onChange={(e) => update({ value: e.target.value })}
                        className="text-xs border rounded px-1.5 py-1 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      >
                        <option value="">Choose…</option>
                        {c.enum_choices.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    ) : c?.type === "datetime" && f.op !== "last_n_days" ? (
                      <input
                        type="date"
                        value={f.value ?? ""}
                        onChange={(e) => update({ value: e.target.value })}
                        className="text-xs border rounded px-1.5 py-1 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      />
                    ) : (
                      <input
                        value={f.value ?? ""}
                        onChange={(e) => update({ value: e.target.value })}
                        placeholder={
                          f.op === "in"
                            ? "a, b, c"
                            : f.op === "last_n_days"
                              ? "30"
                              : "value"
                        }
                        className="text-xs border rounded px-1.5 py-1 w-28 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                      />
                    )}
                    <button
                      onClick={() => setFilters(filters.filter((_, j) => j !== i))}
                      className="ml-auto text-gray-400 hover:text-red-600 text-xs px-1"
                      aria-label="Remove condition"
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Sort + limit */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">
              Order and size
            </h2>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-500 dark:text-gray-400">Sort by</span>
              <select
                value={orderBy?.field || ""}
                onChange={(e) =>
                  setOrderBy(
                    e.target.value
                      ? { field: e.target.value, direction: orderBy?.direction || "asc" }
                      : null,
                  )
                }
                className="border rounded px-1.5 py-1 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              >
                <option value="">(default)</option>
                {t.columns.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
              {orderBy && (
                <select
                  value={orderBy.direction}
                  onChange={(e) =>
                    setOrderBy({ ...orderBy, direction: e.target.value as "asc" | "desc" })
                  }
                  className="border rounded px-1.5 py-1 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="asc">A → Z / oldest first</option>
                  <option value="desc">Z → A / newest first</option>
                </select>
              )}
              <span className="ml-2 text-gray-500 dark:text-gray-400">Max rows</span>
              <input
                type="number"
                min={1}
                max={50000}
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="border rounded px-1.5 py-1 w-20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
          </section>

          {/* Save */}
          <section className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">
              Save it
            </h2>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Report name"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-2 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            />
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100"
            />
            <div className="flex flex-wrap gap-2">
              <ActionButton
                onAction={() => onSave(draft())}
                disabled={!name.trim() || columns.length === 0 || saving}
                className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                pendingLabel="Saving…"
              >
                Save report
              </ActionButton>
              {/* Downloading should not require saving first — plenty of
                  reports are run once and never wanted again. */}
              <ActionButton
                onAction={() => onDownload(draft(), "csv")}
                disabled={columns.length === 0}
                className="text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                pendingLabel="…"
              >
                CSV
              </ActionButton>
              <ActionButton
                onAction={() => onDownload(draft(), "pdf")}
                disabled={columns.length === 0}
                className="text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                pendingLabel="…"
              >
                PDF
              </ActionButton>
            </div>
          </section>
        </div>

        {/* Live preview */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Preview</h2>
            <span className="text-[11px] text-gray-400">
              {previewing
                ? "running…"
                : preview
                  ? // total_rows is the count RETURNED, so it can never be
                    // more than rows.length. `capped` is the server's own
                    // "there are more than this" flag.
                    `${preview.total_rows} row${preview.total_rows === 1 ? "" : "s"}${
                      preview.capped ? " shown — the download has them all" : ""
                    }`
                  : ""}
            </span>
          </div>
          {previewError ? (
            <p className="px-4 py-6 text-sm text-red-600 dark:text-red-400">{previewError}</p>
          ) : !preview ? (
            <p className="px-4 py-6 text-sm text-gray-400">Pick at least one column.</p>
          ) : preview.rows?.length === 0 ? (
            /* An empty result is a real answer, and the commonest reason to
               save a broken report. Say which conditions produced it. */
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-300">No rows match.</p>
              {filters.length > 0 && (
                <p className="mt-1 text-xs text-gray-400">
                  {filters.length} condition{filters.length === 1 ? "" : "s"} applied — try
                  removing one.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[32rem]">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                  <tr>
                    {(preview.columns || []).map((k: string) => (
                      <th
                        key={k}
                        className="text-left px-3 py-2 font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap"
                      >
                        {colOf(k)?.label || k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {/* Rows come back as OBJECTS keyed by column, not positional
                      arrays. row.map() blanked the entire page with
                      "e.map is not a function" — index by the column key. */}
                  {(preview.rows || []).map((row: Record<string, any>, i: number) => (
                    <tr key={i}>
                      {(preview.columns || []).map((k: string) => {
                        const v = row?.[k];
                        return (
                          <td
                            key={k}
                            className="px-3 py-1.5 text-gray-700 dark:text-gray-300 whitespace-nowrap max-w-xs truncate"
                          >
                            {v === null || v === undefined || v === "" ? (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            ) : typeof v === "boolean" ? (
                              v ? "Yes" : "No"
                            ) : colOf(k)?.type === "datetime" ? (
                              /* The API returns zone-less UTC. formatDateTime
                                 handles that; `new Date(str)` would read it as
                                 local and shift every timestamp. Raw, it also
                                 rendered as
                                 "2026-07-19T07:17:49.514215+00:" — truncated
                                 mid-offset by the cell width. */
                              formatDateTime(String(v))
                            ) : (
                              String(v)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
